import { useCallback, useMemo } from 'react';
import { omit } from 'lodash';

import { useTrack } from '@blockworks/platform/services';
import { MultiToggle } from '@blockworks/ui';

import { useVisualizationOptionsContext } from '../../../../context';
import type { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

import { useActiveControls } from './parameter-controls.utils';

type ActiveControlValues = ReturnType<typeof useActiveControls>;
type ParameterControlsProps = Omit<ActiveControlValues, 'setParams' | 'isEnabled'> & {
    setParams?: ActiveControlValues['setParams'];
    disabled?: boolean;
    visualization: VisualizationRendererProps['visualization'];
};

const controlSizeMap = {
    xxs: 'xs',
    xs: 'sm',
} as const;

interface ParameterControlProps extends Omit<ParameterControlsProps, 'activeControls'> {
    parameter: ParameterControlsProps['activeControls'][keyof ParameterControlsProps['activeControls']];
}

const ParameterControl = ({ disabled, visualization, parameter, params, setParams }: ParameterControlProps) => {
    const { layout } = useVisualizationOptionsContext();

    const track = useTrack();

    const onValueChange = useCallback(
        (next: any) => {
            setParams?.((prev: any) => ({
                ...prev,
                [parameter.id]: next ?? prev[parameter.id],
            }));
            track?.(`${visualization.type} > ${parameter.name} > ${next} clicked`, {
                visualization: omit(visualization, ['execution', 'config']),
                parameter,
            });
        },
        [track, visualization, parameter, setParams],
    );

    return (
        <MultiToggle
            disabled={disabled}
            key={`param-${parameter.id}-toolbar`}
            intent="primary"
            ariaLabel={parameter.name}
            size={controlSizeMap[layout.controlSize]}
            onValueChange={onValueChange}
            id={parameter.id}
            value={params[parameter.id]! ?? parameter.views[0]!.value}
            values={parameter.views}
        />
    );
};

const ParameterControls = ({ visualization, setParams, activeControls, disabled, params }: ParameterControlsProps) => {
    const keys = useMemo(() => Object.keys(activeControls ?? {}), [activeControls]);

    if (!keys || keys.length === 0) return null;

    return keys.map(id => {
        const parameter = activeControls[id]!;
        if (parameter.hidden || parameter.views.length <= 1) return null;
        return (
            <ParameterControl
                key={`param-${parameter.id}-toolbar`}
                parameter={parameter}
                visualization={visualization}
                setParams={setParams}
                params={params}
                disabled={disabled}
            />
        );
    });
};

ParameterControls.useActiveControlsState = useActiveControls;

export { ParameterControl, ParameterControls };
